import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { lagerLocationsAPI } from '../services/api';
import { LagerLocation, LagerStorageRow } from '../types';
import { showCustomAlert } from './CustomAlert';
import { useAuth } from '../contexts/AuthContext';
import LagerLocationDialog from './LagerLocationDialog';
import { occupancyColor } from '../utils/siteGeometry';

// 3D prikaz (three.js) se učitava tek kada se otvori kartica
const LagerSite3D = lazy(() => import('./LagerSite3D'));

const sumBy = (rows: LagerStorageRow[], key: 'capacity' | 'occupied' | 'free') =>
  rows.reduce((sum, r) => sum + Number(r[key] || 0), 0);

const LagerStorage: React.FC = () => {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'direktor' || (user?.role === 'lager' && user?.subRole === 'sef_lagera');

  const [locations, setLocations] = useState<LagerLocation[]>([]);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LagerLocation | null>(null);

  const loadLocations = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await lagerLocationsAPI.getAll();
      setLocations(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju lokacija';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setDialogVisible(true);
  };

  const openEdit = (location: LagerLocation) => {
    setEditing(location);
    setDialogVisible(true);
  };

  const handleDelete = (location: LagerLocation) => {
    showCustomAlert.confirm(
      `Da li ste sigurni da želite da obrišete lokaciju "${location.name}"?`,
      async () => {
        try {
          await lagerLocationsAPI.delete(location._id);
          await loadLocations();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju lokacije';
          showCustomAlert.error(msg);
        }
      },
      'Potvrda brisanja'
    );
  };

  const occupancyBar = (occupied: number, capacity: number) => {
    const pct = capacity > 0 ? Math.round((occupied / capacity) * 100) : 0;
    return (
      <div style={{ minWidth: '140px' }}>
        <ProgressBar value={Math.min(pct, 100)} showValue={false} style={{ height: '8px' }} color={occupancyColor(pct)} />
        <small style={{ color: '#64748b' }}>
          {occupied} / {capacity} ({pct}%)
        </small>
      </div>
    );
  };

  const rowExpansionTemplate = (location: LagerLocation) => (
    <div style={{ padding: '0.5rem 1rem' }}>
      <DataTable value={location.rows} emptyMessage="Lokacija nema redova" responsiveLayout="stack" breakpoint="768px">
        <Column field="name" header="Red" />
        <Column field="capacity" header="Mesta za palete" />
        <Column header="Zauzeto" body={(r: LagerStorageRow) => r.occupied ?? 0} />
        <Column header="Slobodno" body={(r: LagerStorageRow) => r.free ?? r.capacity} />
        <Column header="Popunjenost" body={(r: LagerStorageRow) => occupancyBar(Number(r.occupied || 0), r.capacity)} />
      </DataTable>
    </div>
  );

  const actionsBody = (location: LagerLocation) =>
    canManage ? (
      <div className="flex gap-2 justify-content-end">
        <Button icon="pi pi-pencil" className="action-icon-btn" text rounded onClick={() => openEdit(location)} tooltip="Izmeni" />
        <Button
          icon="pi pi-trash"
          className="action-icon-btn"
          text
          rounded
          severity="danger"
          onClick={() => handleDelete(location)}
          tooltip="Obriši"
        />
      </div>
    ) : null;

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Skladište" subTitle="Lokacije i redovi za palete">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel header="Lista" leftIcon="pi pi-list mr-2">
            <div className="flex justify-content-end gap-2" style={{ marginBottom: '1rem' }}>
              {canManage && <Button label="Dodaj lokaciju" icon="pi pi-plus" onClick={openCreate} />}
              <Button label="Osveži" icon="pi pi-refresh" severity="secondary" outlined onClick={loadLocations} />
            </div>

            <DataTable
              value={locations}
              loading={loading}
              dataKey="_id"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
              rowExpansionTemplate={rowExpansionTemplate}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              responsiveLayout="stack"
              breakpoint="768px"
              emptyMessage="Nema lokacija"
            >
              <Column expander style={{ width: '3rem' }} />
              <Column field="name" header="Lokacija" sortable />
              <Column header="Broj redova" body={(l: LagerLocation) => l.rows.length} />
              <Column header="Ukupno mesta" body={(l: LagerLocation) => sumBy(l.rows, 'capacity')} />
              <Column header="Slobodno" body={(l: LagerLocation) => sumBy(l.rows, 'free')} />
              <Column header="Popunjenost" body={(l: LagerLocation) => occupancyBar(sumBy(l.rows, 'occupied'), sumBy(l.rows, 'capacity'))} />
              <Column
                header="Na mapi"
                body={(l: LagerLocation) =>
                  l.layout ? <i className="pi pi-check" style={{ color: '#22c55e' }} /> : <i className="pi pi-minus" style={{ color: '#94a3b8' }} />
                }
                style={{ width: '90px' }}
              />
              {canManage && <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />}
            </DataTable>
          </TabPanel>

          <TabPanel header="3D mapa" leftIcon="pi pi-box mr-2">
            {activeTab === 1 && (
              <Suspense
                fallback={
                  <div className="flex justify-content-center" style={{ padding: '3rem' }}>
                    <ProgressSpinner />
                  </div>
                }
              >
                <LagerSite3D locations={locations} canManage={canManage} onChanged={loadLocations} onEdit={openEdit} />
              </Suspense>
            )}
          </TabPanel>
        </TabView>
      </Card>

      <LagerLocationDialog
        visible={dialogVisible}
        location={editing}
        onHide={() => setDialogVisible(false)}
        onSaved={() => {
          setDialogVisible(false);
          void loadLocations();
        }}
      />
    </div>
  );
};

export default LagerStorage;
